import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { IdentityTypeCode, useMobile } from '@verifime/utils';
import { withClarityTheme } from '@verifime/styles';
import { ComponentType, ReactNode, useState } from 'react';
import { StickyActionBar } from '../../StickyActionBar/StickyActionBar';
import { EntityTypeGroup, entityTypeTileGridSx } from '../EntityTypeGroup';
import { IndividualTile } from '../IndividualTile';
import { PartnershipTile } from '../PartnershipTile';
import { PrivateCompanyTile } from '../PrivateCompanyTile';
import { PublicCompanyTile } from '../PublicCompanyTile';
import { RegulatedTrustTile } from '../RegulatedTrustTile';
import { SmsfTile } from '../SmsfTile';
import { SoleTraderTile } from '../SoleTraderTile';
import { UnregulatedTrustTile } from '../UnregulatedTrustTile';
import {
  DEFAULT_ENTITY_TYPE_GROUP_ORDER,
  ENTITY_TYPE_GROUPS,
  type TEntityChoice,
  type TEntityTypeGroupKey,
  type TSelectableEntityType,
} from './entityTypeGroups';
import {
  CANCEL_LABEL,
  CONTINUE_LABEL,
  ENTITY_TYPE_SELECTOR_HEADING,
  SAVING_LABEL,
} from './entityTypeSelectorCopy';

/**
 * How the Individual ("just me") tile shows: not at all, choosable, or visible but not choosable.
 */
export type TIndividualTileMode = 'hidden' | 'enabled' | 'disabled';

type EntityTypeSelectorBaseProps = {
  /**
   * Called with the choice when Continue is clicked (only possible once a choice is made)
   */
  onContinue: (choice: TEntityChoice) => void;
  /**
   * Called when Cancel is clicked. Leave it out and the selector shows no Cancel button, for a
   * caller that should not offer a way out.
   */
  onCancel?: () => void;
  /**
   * The Individual tile: `hidden` (the default), `enabled`, or `disabled` (shown but cannot be
   * chosen). Unless it is `enabled`, an `individual` selection counts as no choice: nothing shows
   * selected and Continue stays disabled.
   */
  individualTile?: TIndividualTileMode;
  /**
   * Optional content under the heading, in body text
   */
  intro?: ReactNode;
  /**
   * While true, Continue reads "Loading..." and both buttons are disabled
   */
  isSaving?: boolean;
  /**
   * The order of the entity groups. Defaults to Organisation, Trust, Business.
   */
  groupOrder?: readonly TEntityTypeGroupKey[];
  /**
   * Optional data-testid for the root element
   */
  dataTestId?: string;
};

type EntityTypeSelectorUncontrolledProps = {
  /**
   * Leave `selected` out and the selector keeps its own selection, starting from none.
   */
  selected?: undefined;
  /**
   * Called with the choice when a tile is clicked
   */
  onSelect?: (choice: TEntityChoice) => void;
};

type EntityTypeSelectorControlledProps = {
  /**
   * The current choice, or null for none. Passing it (including null) makes the selection
   * controlled: the selector shows this value and reports clicks through `onSelect` only.
   */
  selected: TEntityChoice | null;
  /**
   * Called with the choice when a tile is clicked. Required with `selected`, since a controlled
   * selection that cannot change would make every click do nothing.
   */
  onSelect: (choice: TEntityChoice) => void;
};

export type EntityTypeSelectorProps = EntityTypeSelectorBaseProps &
  (EntityTypeSelectorUncontrolledProps | EntityTypeSelectorControlledProps);

type TTileProps = { selected?: boolean; onClick?: () => void };

// Typed against the props the selector passes. A tile that gains a required prop no longer fits
// `ComponentType<TTileProps>`, so the compiler rejects this map (checked: adding a required prop
// to SoleTraderTileProps fails with TS2418 here), rather than the tile rendering without it.
const ENTITY_TILES: Record<TSelectableEntityType, ComponentType<TTileProps>> = {
  [IdentityTypeCode.Private_Company]: PrivateCompanyTile,
  [IdentityTypeCode.Public_Company]: PublicCompanyTile,
  [IdentityTypeCode.Sole_Trader]: SoleTraderTile,
  [IdentityTypeCode.Partnership]: PartnershipTile,
  [IdentityTypeCode.SMSF]: SmsfTile,
  [IdentityTypeCode.Trust_Unregulated]: UnregulatedTrustTile,
  [IdentityTypeCode.Trust_Regulated]: RegulatedTrustTile,
};

type EntityTypeSelectorActionsProps = Readonly<{
  isSaving: boolean;
  canContinue: boolean;
  onCancel: (() => void) | undefined;
  onContinue: () => void;
}>;

/**
 * Cancel (only when there is an `onCancel`) and Continue. At 600px and up they are a plain row;
 * below it they sit in the shared StickyActionBar, which pins them to the bottom of the viewport.
 */
const EntityTypeSelectorActions = ({
  isSaving,
  canContinue,
  onCancel,
  onContinue,
}: EntityTypeSelectorActionsProps) => {
  const isMobile = useMobile();
  const { spacing } = useTheme().semantic;

  const actions = (
    <Stack
      direction={['column-reverse', 'row']}
      // Continue stays on the right whether or not Cancel sits opposite it.
      justifyContent={onCancel ? 'space-between' : 'flex-end'}
      alignItems={['stretch', 'center']}
      gap={(theme) => theme.semantic.spacing.lg}
    >
      {onCancel && (
        <Button
          variant="outlined"
          data-action="cancel-entity-selection"
          disabled={isSaving}
          onClick={onCancel}
        >
          {CANCEL_LABEL}
        </Button>
      )}
      <Button
        variant="contained"
        data-action="continue-entity-selection"
        disabled={!canContinue || isSaving}
        onClick={onContinue}
      >
        {isSaving ? SAVING_LABEL : CONTINUE_LABEL}
      </Button>
    </Stack>
  );

  if (!isMobile) {
    return actions;
  }

  // The page around the selector pads its content by `spacing.xl` on every side, so
  // `parentPaddingX` and `parentPaddingBottom` negate that to reach the pane edges, and
  // `contentInset` insets the buttons by the same amount to keep the column of the tiles above
  // them (see the prop docs on StickyActionBar for why each is explicit).
  return (
    <StickyActionBar
      parentPaddingX={spacing.xl}
      parentPaddingBottom={spacing.xl}
      contentInset={spacing.xl}
    >
      {actions}
    </StickyActionBar>
  );
};

/**
 * EntityTypeSelector is the one entity type picker: a heading, optional intro, optional Individual
 * tile, the entity groups, and Cancel and Continue. Its defaults are the add-entity page; the
 * choose screen turns on the Individual tile and the intro.
 *
 * Presentational only (ADR 50): the caller owns the page frame, the save and the navigation. One
 * assumption about that frame: below 600px the actions pin to the bottom of the viewport, and the
 * footer negates the page's padding to reach the pane edges, so the page must pad its content by
 * `spacing.xl` on every side (as `EntitySelectionPageLayout` and the add-entity page do).
 */
const EntityTypeSelectorComponent = ({
  onContinue,
  onCancel,
  selected,
  onSelect,
  individualTile = 'hidden',
  intro,
  isSaving = false,
  groupOrder = DEFAULT_ENTITY_TYPE_GROUP_ORDER,
  dataTestId = 'entity-type-selector',
}: Readonly<EntityTypeSelectorProps>) => {
  const [ownSelected, setOwnSelected] = useState<TEntityChoice | null>(null);
  const isControlled = selected !== undefined;
  const current = isControlled ? selected : ownSelected;
  // Only an enabled Individual tile can be chosen, so otherwise a stale 'individual' choice counts
  // as no choice: nothing shows selected and Continue stays disabled.
  const effectiveSelected =
    current === 'individual' && individualTile !== 'enabled' ? null : current;

  const handleSelect = (choice: TEntityChoice) => {
    if (!isControlled) {
      setOwnSelected(choice);
    }
    onSelect?.(choice);
  };

  const handleContinue = () => {
    if (effectiveSelected !== null) {
      onContinue(effectiveSelected);
    }
  };

  return (
    <Stack data-testid={dataTestId} gap={(theme) => theme.semantic.spacing.xl}>
      <Stack gap={(theme) => theme.semantic.spacing.sm}>
        <Typography variant="h3">{ENTITY_TYPE_SELECTOR_HEADING}</Typography>
        {intro && (
          <Typography variant="body1" component="div">
            {intro}
          </Typography>
        )}
      </Stack>

      <Stack gap={(theme) => theme.semantic.spacing.lg}>
        {individualTile !== 'hidden' && (
          <Box sx={entityTypeTileGridSx}>
            <IndividualTile
              selected={effectiveSelected === 'individual'}
              onClick={() => handleSelect('individual')}
              disabled={individualTile === 'disabled'}
            />
          </Box>
        )}

        {groupOrder.map((groupKey) => {
          const group = ENTITY_TYPE_GROUPS[groupKey];
          return (
            <EntityTypeGroup key={groupKey} title={group.title}>
              {group.entityTypes.map((entityType) => {
                const Tile = ENTITY_TILES[entityType];
                return (
                  <Tile
                    key={entityType}
                    selected={effectiveSelected === entityType}
                    onClick={() => handleSelect(entityType)}
                  />
                );
              })}
            </EntityTypeGroup>
          );
        })}
      </Stack>

      <EntityTypeSelectorActions
        isSaving={isSaving}
        canContinue={effectiveSelected !== null}
        onCancel={onCancel}
        onContinue={handleContinue}
      />
    </Stack>
  );
};

export const EntityTypeSelector = withClarityTheme(EntityTypeSelectorComponent);
